import React from 'react'
import styles from './ClubPoiEditor.scss'
import { PoiMap } from './PoiMap'
import { Form } from './Form'
import Immutable from 'immutable'
import fetchJSON from 'lib/fetchJSON'
import L from 'leaflet'
import { PointOfInterest, deserializePointOfInterest } from 'lib/api/records'
import { ModalOverlay } from 'common/styleguide/ModalOverlay'
import { CircleSpinner } from 'common/spinners'
import { getCurrentUser, getPhotoUploadMaxConcurrency } from 'lib/utils'
import PromisePool from 'es6-promise-pool'

export class ClubPoiEditor extends React.PureComponent {
  static defaultProps = {
    currentUser: getCurrentUser(),
    clubId: null, // integer, required
    poiId: null, // integer
    onRequestClose: null, // func()
    onEditSuccess: null, // func(object)
    tags: [] // array[string]
  }

  constructor (props) {
    super(props)

    const { lat, lng } = props.currentUser
    this.state = {
      poi: new PointOfInterest(),
      routes: [],
      bounds: null,
      center: [lat || 45.53580, lng || -122.65868], // default to the RWGPS office
      searchLocation: null,
      errors: null,
      saveSuccess: false,
      gmapsLoaded: false,
      pendingUploads: 0,
      photoIds: Immutable.Set([]),
      files: Immutable.List()
    }
  }

  componentDidMount () {
    const { poiId } = this.props

    if (poiId) {
      poiId && this.fetchPoi(poiId)
    }

    if (window.google && window.google.maps) {
      this.setState({ gmapsLoaded: true })
    } else {
      this.lazyLoadGoogle()
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (!prevState.saveSuccess && this.state.saveSuccess) {
      const { onRequestClose } = this.props
      onRequestClose && onRequestClose()
    }
  }

  lazyLoadGoogle () {
    console.log('Starting Google Maps API lazy load')
    const script = document.createElement('script')
    const apiKey = window.rwgps.config.gmapsApiKey
    const version = '3.28'
    const mapLanguage = window.rwgps.config.gmapsMapLanguage
    const mapRegion = ''
    const src = '//maps.google.com/maps/api/js' +
      '?' + apiKey +
      '&v=' + version +
      '&language=' + mapLanguage +
      '&region=' + mapRegion
    window.onLazyLoadGoogleComplete = () => {
      console.log('Google Maps lazy load completed, running callbacks')
      // clean up
      delete window.onLazyLoadGoogleComplete
      this.setState({ gmapsLoaded: true })
    }
    script.setAttribute('src', src)
    script.setAttribute('onload', 'window.onLazyLoadGoogleComplete()')
    document.body.appendChild(script)
  }

  onMapBoundsChange = (bounds) => {
    this.setState({ bounds })
  }

  onLatLngChange = (latLng) => {
    const { poi } = this.state
    this.setState({ poi: poi.merge(latLng) })
  }

  onPoiTypeIdChange = (typeId) => {
    const { poi } = this.state
    this.setState({ poi: poi.merge({ typeId }) })
  }

  serializePoiUpdate = (poi, routeIds, poiIds) => {
    const { name, description, url, lat, lng, typeId, tagNames } = poi
    const poiAttrs = {
      name,
      poi_type: typeId,
      tag_names: tagNames,
      description,
      url,
      lat,
      lng
    }

    return { point_of_interest: poiAttrs, route_ids: routeIds, poi_ids: poiIds }
  }

  addPhotos = ({ point_of_interest: { id } }) => {
    const { files } = this.state
    if (files.size === 0) { this.setState({ saveSuccess: true }); return }

    this.setState((prevState) => ({ pendingUploads: prevState.pendingUploads + files.size }))
    let index = 0
    const nextUpload = () => {
      if (!files.get(index)) { return null }
      const nextUpload = files.get(index)
      index++

      const body = new window.FormData()
      body.append('file', nextUpload)
      body.append('parent_type', 'point_of_interest')
      body.append('parent_id', id)

      return fetchJSON('/photos.json', null, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(() => this.setState((prevState) => ({
        pendingUploads: prevState.pendingUploads - 1,
        saveSuccess: prevState.pendingUploads === 1
      })))
    }
    const pool = new PromisePool(nextUpload, getPhotoUploadMaxConcurrency())

    pool.start()
  }

  savePoi = ({ poi: newAttrs, routeIds, poiIds }) => {
    const poi = this.state.poi.merge(newAttrs)
    const { clubId, onEditSuccess } = this.props
    if (!clubId) { throw new Error(`Invalid club id '${clubId}'`) }

    let url, method
    if (poi.id) {
      url = `/organizations/${clubId}/points_of_interest/${poi.id}.json`
      method = 'PATCH'
    } else {
      url = `/organizations/${clubId}/points_of_interest.json`
      method = 'POST'
    }
    const body = this.serializePoiUpdate(poi, routeIds, poiIds)
    fetchJSON(url, body, { method })
      .then(data => {
        this.addPhotos(data)
        onEditSuccess && onEditSuccess(data)
      })
      .catch(e => {
        console.error(e)
        this.setState({ errors: Immutable.fromJS(e.errors) })
      })
  }

  fetchPoi = (poiId) => {
    const { clubId } = this.props
    if (!clubId) { throw new Error(`Invalid club id '${clubId}'`) }
    if (!poiId) { throw new Error(`Invalid POI id '${poiId}'`) }
    const poiUrl = `/clubs/${clubId}/points_of_interest/${poiId}.json?include_routes=1`

    return fetchJSON(poiUrl)
      .then(data => {
        const poi = deserializePointOfInterest(data.point_of_interest)
        const center = [poi.lat, poi.lng]
        this.setState({ poi, center, routes: data.routes, photoIds: Immutable.Set(poi.photoIds) })
      })
      .catch(e => {
        console.error(e)
        this.setState({ poi: 'error' })
      })
  }

  findLocation = (address) => {
    if (!this.state.gmapsLoaded || !address || address === '') {
      return
    }
    const { bounds: lBounds, center } = this.state
    const bounds = lBounds ? {
      north: lBounds.getNorth(),
      east: lBounds.getEast(),
      south: lBounds.getSouth(),
      west: lBounds.getWest()
    } : {
      // if we just have a center latlng, use it as the bounds. adding a buffer is not required.
      north: center[0],
      east: center[1],
      south: center[0],
      west: center[1]
    }
    if (!this._geocoder) {
      this._geocoder = new window.google.maps.Geocoder()
    }
    const params = {
      address,
      bounds
    }
    this._geocoder.geocode(params, (results, status) => {
      if (results.length > 0) {
        const geometry = results[0].geometry
        const viewport = geometry.viewport
        const sw = viewport.getSouthWest()
        const ne = viewport.getNorthEast()
        const bounds = L.latLngBounds(L.latLng(sw.lat(), sw.lng()), L.latLng(ne.lat(), ne.lng()))
        this.setState({
          center: null,
          bounds,
          poi: this.state.poi.merge(bounds.getCenter())
        })
      } else {
        // TODO: Alert user there were no results
      }
    })
  }

  onPhotosDrop = (files) => {
    this.setState({ files: this.state.files.concat(files) })
  }

  onPhotoRemoved = (pid) => {
    fetchJSON(`/photos/${pid}.json`, null, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(() => {
      this.setState({ photoIds: this.state.photoIds.delete(pid) })
    })
  }

  onFileRemoved = (e, idx) => {
    e.preventDefault()
    const { files } = this.state

    this.setState({ files: files.delete(idx) })
  }

  render () {
    const { combinePois, poiId, onRequestClose, tags } = this.props
    const { poi, routes, bounds, center, photoIds, files, errors } = this.state
    const latLng = poi.lat && poi.lng && { lat: poi.lat, lng: poi.lng }
    const poiFetchFinished = poi === 'error' || poi.id
    const loaded = !poiId || poiFetchFinished
    const viewport = bounds ? { bounds } : { center }

    return (
      <ModalOverlay contentPadding={false} shouldCloseOnOverlayClick={false} onRequestClose={onRequestClose} maxWidth='875px' hideOverflow>
        <div className={styles.ClubPoiEditor}>
          <div className={styles.horizontalColumns}>
            {!loaded && <CircleSpinner size={50} />}
            {loaded &&
              <PoiMap
                {...viewport}
                onBoundsChange={this.onMapBoundsChange}
                onLatLngChange={this.onLatLngChange}
                onPoiTypeIdChange={this.onPoiTypeIdChange}
                latLng={latLng}
                poiTypeId={poi.typeId}
              />}
            {loaded &&
              <Form
                onSave={this.savePoi}
                onCancel={onRequestClose}
                onLocationSearch={this.findLocation}
                onPhotosDrop={this.onPhotosDrop}
                onPhotoRemoved={this.onPhotoRemoved}
                onFileRemoved={this.onFileRemoved}
                photoIds={photoIds}
                files={files}
                poi={poi}
                combinePois={combinePois}
                fetchPoi={this.fetchPoi}
                unsetBounds={() => this.setState({ bounds: null })}
                routes={routes}
                tags={tags}
                errors={errors}
              />}
          </div>
        </div>
      </ModalOverlay>
    )
  }
}
